import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";

// Pages
import AdminUsers from "./pages/AdminUsers";
import Agreements from "./pages/Agreements";
import ArticleDetail from "./pages/ArticleDetail";
import Contact from "./pages/Contact";
import Dashboard from "./pages/Dashboard";
import GettingStarted from "./pages/GettingStarted";
import HowItWorks from "./pages/HowItWorks";
import Index from "./pages/Index";
import IntroAddJob from "./pages/IntroAddJob";
import IntroGettingStarted from "./pages/IntroGettingStarted";
import Industry from "./pages/Industry";
import InterviewPage from "./pages/InterviewPage";
import Interviews from "./pages/Interviews";
import JobDetails from "./pages/JobDetails";
import JobForm from "./pages/JobForm";
import Jobs from "./pages/Jobs";
import Learn from "./pages/Learn";
import LinkedInCallback from "./pages/LinkedInCallback";
import Login from "./pages/Login";
import Me from "./pages/Me";
import NextSteps from "./pages/NextSteps";
import NotFound from "./pages/NotFound";
import Pitch from "./pages/Pitch";
import PitchAnalysis from "./pages/PitchAnalysis";
import PitchNew from "./pages/PitchNew";
import Practice from "./pages/Practice";
import PublicArticleDetail from "./pages/PublicArticleDetail";
import PublicLearn from "./pages/PublicLearn";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Resume from "./pages/Resume";
import SalaryNegotiationPage from "./pages/SalaryNegotiationPage";
import SalaryNegotiations from "./pages/SalaryNegotiations";
import Settings from "./pages/Settings";
import Subscription from "./pages/Subscription";
import Todos from "./pages/Todos";
import BehavioralInterviewQuestionsPage from "./pages/seo/BehavioralInterviewQuestionsPage";
import CommonInterviewMistakesPage from "./pages/seo/CommonInterviewMistakesPage";
import CommonInterviewQuestionsPage from "./pages/seo/CommonInterviewQuestionsPage";
import GreatestWeaknessPage from "./pages/seo/GreatestWeaknessPage";
import HowToPrepareInterviewPage from "./pages/seo/HowToPrepareInterviewPage";
import InterviewTipsPage from "./pages/seo/InterviewTipsPage";
import MockInterviewPracticePage from "./pages/seo/MockInterviewPracticePage";
import QuestionsToAskInterviewerPage from "./pages/seo/QuestionsToAskInterviewerPage";
import StarMethodPage from "./pages/seo/StarMethodPage";
import TellMeAboutYourselfPage from "./pages/seo/TellMeAboutYourselfPage";
import WhyShouldWeHireYouPage from "./pages/seo/WhyShouldWeHireYouPage";

// Providers
import { AuthProvider } from "./contexts/AuthContext";
import { ProfileProvider } from "./contexts/ProfileContext";
import { ReadinessProvider } from "./contexts/ReadinessContext";

// Route protection components
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ProfileProvider>
            <ReadinessProvider>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/intro/getting-started" element={<IntroGettingStarted />} />
                <Route path="/intro/add-job" element={<IntroAddJob />} />
                <Route path="/tell-me-about-yourself" element={<TellMeAboutYourselfPage />} />
                <Route path="/greatest-weakness" element={<GreatestWeaknessPage />} />
                <Route path="/why-should-we-hire-you" element={<WhyShouldWeHireYouPage />} />
                <Route path="/behavioral-interview-questions" element={<BehavioralInterviewQuestionsPage />} />
                <Route path="/star-method" element={<StarMethodPage />} />
                <Route path="/common-interview-questions" element={<CommonInterviewQuestionsPage />} />
                <Route path="/how-to-prepare-for-an-interview" element={<HowToPrepareInterviewPage />} />
                <Route path="/interview-tips" element={<InterviewTipsPage />} />
                <Route path="/common-interview-mistakes" element={<CommonInterviewMistakesPage />} />
                <Route path="/questions-to-ask-interviewer" element={<QuestionsToAskInterviewerPage />} />
                <Route path="/mock-interview-practice" element={<MockInterviewPracticePage />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/agreements" element={<Agreements />} />
                <Route path="/linkedin/callback" element={<LinkedInCallback />} />
                <Route path="/public-learn" element={<PublicLearn />} />
                <Route path="/public-learn/:slug" element={<PublicArticleDetail />} />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <PublicRoute>
                      <ResetPassword />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/register"
                  element={
                    <PublicRoute>
                      <Register />
                    </PublicRoute>
                  }
                />

                {/* Protected routes */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/getting-started"
                  element={
                    <ProtectedRoute>
                      <GettingStarted />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/next-steps"
                  element={
                    <ProtectedRoute>
                      <NextSteps />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/me"
                  element={
                    <ProtectedRoute>
                      <Me />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/todos"
                  element={
                    <ProtectedRoute>
                      <Todos />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/interviews"
                  element={
                    <ProtectedRoute>
                      <Interviews />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs"
                  element={
                    <ProtectedRoute>
                      <Jobs />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/new"
                  element={
                    <ProtectedRoute>
                      <JobForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:jobId"
                  element={
                    <ProtectedRoute>
                      <JobDetails />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:jobId/negotiation"
                  element={
                    <ProtectedRoute>
                      <SalaryNegotiationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/:jobId/negotiation/:negotiationId"
                  element={
                    <ProtectedRoute>
                      <SalaryNegotiationPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/interview/:interviewId"
                  element={
                    <ProtectedRoute>
                      <InterviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/jobs/edit/:jobId"
                  element={
                    <ProtectedRoute>
                      <JobForm />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/resume"
                  element={
                    <ProtectedRoute>
                      <Resume />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/practice"
                  element={
                    <ProtectedRoute>
                      <Practice />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/practice/:jobId"
                  element={
                    <ProtectedRoute>
                      <Practice />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/industry"
                  element={
                    <ProtectedRoute>
                      <Industry />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/subscription"
                  element={
                    <ProtectedRoute>
                      <Subscription />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pitch"
                  element={
                    <ProtectedRoute>
                      <Pitch />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pitch/new"
                  element={
                    <ProtectedRoute>
                      <PitchNew />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/pitch/:pitchId"
                  element={
                    <ProtectedRoute>
                      <PitchAnalysis />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/salary-negotiations"
                  element={
                    <ProtectedRoute>
                      <SalaryNegotiations />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/learn"
                  element={
                    <ProtectedRoute>
                      <Learn />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/learn/:slug"
                  element={
                    <ProtectedRoute>
                      <ArticleDetail />
                    </ProtectedRoute>
                  }
                />

                {/* Admin routes */}
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute>
                      <AdminUsers />
                    </ProtectedRoute>
                  }
                />

                {/* Catch-all route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </ReadinessProvider>
          </ProfileProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
